import type { ClassNamesConfig, GroupBase } from "react-select";

/**
 * Estilos de react-select compartidos.
 *
 * Había dos sistemas paralelos y divergentes: un objeto `styles` con hexes
 * fijos (sin modo oscuro y con placeholders de bajo contraste) en la página de
 * Eventos, y un bloque `classNames` en el widget del panel. Ahora los dos usan
 * esto, que replica exactamente el input de la app: alto de 40px, radio de
 * campo y los mismos estados de borde.
 *
 * Requiere la prop `unstyled` en el <Select /> para que react-select no inyecte
 * sus estilos por defecto.
 */
export function selectClassNames<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(): ClassNamesConfig<Option, IsMulti, Group> {
  return {
    control: ({ isFocused }) =>
      [
        "flex h-10 w-full cursor-pointer items-center px-3",
        "rounded-field border bg-white text-sm font-medium text-gray-900",
        "transition-colors duration-150 dark:bg-navy-900 dark:text-white",
        isFocused
          ? "border-brand-300 dark:border-brand-500"
          : "border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20",
      ].join(" "),

    valueContainer: () => "m-0 flex items-center justify-start gap-2 p-0",
    singleValue: () => "m-0 text-gray-900 dark:text-white",
    placeholder: () => "m-0 text-gray-600",
    input: () => "m-0 p-0 text-gray-900 dark:text-white",

    indicatorsContainer: () => "flex items-center",
    indicatorSeparator: () => "hidden",
    dropdownIndicator: () => "p-0 text-gray-600",

    menu: () =>
      "mt-2 rounded-card border border-gray-200/80 bg-white p-1.5 shadow-pop dark:border-white/10 dark:bg-navy-800",
    menuList: () => "flex flex-col gap-0.5",

    option: ({ isFocused, isSelected }) =>
      [
        "cursor-pointer rounded-control px-3 py-2 text-sm transition-colors",
        isSelected
          ? "bg-brand-500 font-semibold text-white"
          : isFocused
          ? "bg-gray-50 text-gray-900 dark:bg-white/5 dark:text-white"
          : "bg-white text-gray-900 dark:bg-navy-800 dark:text-gray-200",
      ].join(" "),

    noOptionsMessage: () => "px-3 py-2 text-sm text-gray-700",
  };
}

export default selectClassNames;
